import React, { useEffect, useState, useCallback } from 'react'
import { Grid, Box, Paper, Chip, Tooltip, Typography } from "@mui/material"
import * as actions from "../Releases/actions";
import moment from "moment"
import { useNavigate } from "react-router-dom"
import SelectCMP from "react-select"
import { releaseFilters } from '../../constants'
import Pagination from '@mui/lab/Pagination';
import _ from "lodash";
import StatusBadge from '../../components/StatusBadge';
import EmptyState from '../../components/EmptyState';
import SearchInput from '../../components/SearchInput';
import darkSelectStyles from '../../theme/reactSelectStyles';
import { Video, Calendar, User, Mail, Music } from 'lucide-react';

export default function VideoReleasesHistory(props) {

    const navigate = useNavigate();
    const [releases, setReleases] = useState([])
    const [filters, setFilters] = useState({
        sort: 'approved',
        limit: 50,
        offset: 0,
        search: '',
        page: 1
    })
    const [count, setCount] = useState(0)
    const [search, setSearch] = useState('');
    const [selectedFilter, setSelectedFilter] = useState({
        label: 'Approved',
        value: 'approved'
    });
    const delaySearch = useCallback(_.debounce(filters => onGetNewReleases(filters), 500), []);

    useEffect(() => {
        document.title = 'VR | Video Release History';
        return () => { document.title = 'Fronic Media Admin'; };
    }, []);

    useEffect(() => {
        onGetNewReleases(filters)
    }, [filters]);

    useEffect(() => {
        if(!!search) {
            setCount(releases.length || 0)
        }
    });

    const onPageChange = (event, page) => {
        const offset = (page - 1) * filters.limit
        setFilters({ ...filters, page, offset })
    }

    const onGetNewReleases = (filters) => {
        props.setLoader(true)
        actions.getVReleasesAdmin(filters).then(res => {
            props.setLoader(false)
            setReleases(res.body)
            setCount(res.total)
        }).catch(err => {
            props.setLoader(false)
        })
    }

    const searchReleases = (value) => {
        setSearch(value)
        if (!value) {
            onGetNewReleases(filters)
        } else {
            delaySearch({ ...filters, search: value })
        }
    }

    const onChangeSelect = (e) => {
        setSelectedFilter(e)
        setFilters({ ...filters, sort: e.value })
    }

    let totalPages = Math.ceil(Number(count) / filters.limit)

    return (
        <Grid container>
            <Grid item xs={12}>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={3}>
                    <Box sx={{ flex: 1, maxWidth: 420 }}>
                        <SearchInput value={search} onChange={(e) => searchReleases(e.target.value)} placeholder="Search by title, artist, or email..." />
                    </Box>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Box sx={{ minWidth: 160 }}>
                            <SelectCMP
                                options={releaseFilters}
                                value={selectedFilter}
                                onChange={e => onChangeSelect(e)}
                                styles={darkSelectStyles}
                            />
                        </Box>
                        <Chip
                            icon={<Video size={14} />}
                            label={`${count} Release${count !== 1 ? 's' : ''}`}
                            sx={{
                                backgroundColor: 'rgba(108, 99, 255, 0.12)',
                                color: '#6C63FF',
                                border: '1px solid rgba(108, 99, 255, 0.25)',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                height: 34,
                                flexShrink: 0,
                                '& .MuiChip-icon': { color: '#6C63FF' },
                            }}
                        />
                    </Box>
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Box className="approved-release-list">
                    {releases.length ? releases.map((item, key) => (
                        <Paper
                            key={item.video_id || key}
                            className="approved-release-card"
                            onClick={() => navigate(`/view_video_release/${item.video_id}`, { state: { from: '/video_releases/history' } })}
                        >
                            <div className="approved-release-card__artwork">
                                <div className="approved-release-card__artwork-fallback">
                                    <Video size={20} color="#64748B" />
                                    <span>Video</span>
                                </div>
                            </div>

                            <div className="approved-release-card__body">
                                <div className="approved-release-card__top">
                                    <div className="approved-release-card__title-row">
                                        <Typography className="approved-release-card__title">
                                            {item.release_title || 'Untitled Release'}
                                        </Typography>
                                        <span className="approved-release-card__id">FM-VR-{item.video_id}</span>
                                    </div>
                                    <div className="approved-release-card__status">
                                        <StatusBadge status={item.status} />
                                    </div>
                                </div>

                                <div className="approved-release-card__meta">
                                    <Tooltip title="Song Title" arrow placement="top">
                                        <span className="approved-release-card__meta-item approved-release-card__meta-item--tracks">
                                            <Music size={13} />
                                            {item.audio_title || '-'}
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Artist" arrow placement="top">
                                        <span className="approved-release-card__meta-item">
                                            <User size={13} />
                                            {item.name || '-'}
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Email" arrow placement="top">
                                        <span className="approved-release-card__meta-item">
                                            <Mail size={13} />
                                            {item.email || '-'}
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Release Date" arrow placement="top">
                                        <span className="approved-release-card__meta-item">
                                            <Calendar size={13} />
                                            {moment(Number(item.release_date)).format('DD MMM YYYY')}
                                        </span>
                                    </Tooltip>
                                </div>
                            </div>

                            <div className="approved-release-card__arrow">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="9 18 15 12 9 6" />
                                </svg>
                            </div>
                        </Paper>
                    )) : (
                        <Paper sx={{ p: 0 }}>
                            <table style={{ width: '100%' }}><tbody>
                                <EmptyState message="No Video Releases Found" colSpan={1} />
                            </tbody></table>
                        </Paper>
                    )}
                </Box>

                {totalPages > 1 && (
                    <Box display="flex" justifyContent="center" mt={3}>
                        <Pagination
                            count={totalPages}
                            showFirstButton
                            showLastButton
                            onChange={onPageChange}
                            page={filters.page || 1}
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    color: '#94A3B8',
                                    '&.Mui-selected': {
                                        backgroundColor: 'rgba(108, 99, 255, 0.2)',
                                        color: '#6C63FF',
                                    },
                                },
                            }}
                        />
                    </Box>
                )}
            </Grid>
        </Grid>
    )
}
